/** Dados da empresa (CNPJ, razão social, contato, endereço) e situação do plano. */
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Company } from '@berytus/contracts';
import { api, dateTime, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { can } from '@berytus/contracts';

const PLAN: Record<Company['plan'], string> = { trial: 'Período de teste', paid: 'Plano contratado', internal: 'Cliente Berytus' };
type Addr = Company['address'];
const ADDR: { k: keyof Addr; label: string; max?: number }[] = [
  { k: 'zip', label: 'CEP', max: 10 }, { k: 'street', label: 'Logradouro' }, { k: 'number', label: 'Número', max: 20 },
  { k: 'complement', label: 'Complemento' }, { k: 'district', label: 'Bairro' }, { k: 'city', label: 'Cidade' }, { k: 'state', label: 'UF', max: 2 },
];

export function CompanyPage() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['company'], queryFn: () => api<Company>('/account/company') });
  const [f, setF] = useState<{ legalName: string; name: string; phone: string; contactEmail: string; address: Addr; taxRegime: string; orderStockMode: string; invoiceXmlUrl: string } | null>(null);
  useEffect(() => {
    if (q.data && !f) setF({ legalName: q.data.legalName ?? '', name: q.data.name, phone: q.data.phone ?? '', contactEmail: q.data.contactEmail ?? '', address: q.data.address, taxRegime: q.data.taxRegime ?? '', orderStockMode: q.data.orderStockMode ?? '', invoiceXmlUrl: q.data.invoiceXmlUrl ?? '' });
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => api('/account/company', {
      method: 'PUT',
      json: { ...f, phone: f!.phone || null, contactEmail: f!.contactEmail || null, address: Object.fromEntries(Object.entries(f!.address).filter(([, v]) => v)), taxRegime: f!.taxRegime || null, orderStockMode: f!.orderStockMode || null, invoiceXmlUrl: f!.invoiceXmlUrl.trim() || null },
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company'] }); qc.invalidateQueries({ queryKey: ['me'] }); },
  });
  const canEdit = can(me.tenant.access, 'settings', 'write');
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };

  if (q.isPending || !f) return q.error ? <ErrorBox error={q.error} /> : <Loading />;
  const c = q.data!;
  const t = c.trial;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dados da empresa</h1>
          <p>Cadastro da empresa na Berytus. O CNPJ identifica a conta e não pode ser alterado.</p>
        </div>
      </div>

      <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
        <form className="panel" onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Empresa</h2>
          <div className="field"><label>CNPJ</label><input className="input" value={c.cnpj ?? '—'} disabled /></div>
          <div className="field"><label htmlFor="legal">Razão social</label><input className="input" id="legal" required minLength={3} disabled={!canEdit} value={f.legalName} onChange={(e) => setF({ ...f, legalName: e.target.value })} /></div>
          <div className="field"><label htmlFor="trade">Nome fantasia</label><input className="input" id="trade" required minLength={2} disabled={!canEdit} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div className="field"><label htmlFor="phone">Telefone</label><input className="input" id="phone" disabled={!canEdit} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div className="field"><label htmlFor="mail">E-mail de contato</label><input className="input" id="mail" type="email" disabled={!canEdit} value={f.contactEmail} onChange={(e) => setF({ ...f, contactEmail: e.target.value })} /></div>
          </div>
          <div className="field">
            <label htmlFor="regime">Regime tributário</label>
            <select className="select" id="regime" disabled={!canEdit} value={f.taxRegime} onChange={(e) => setF({ ...f, taxRegime: e.target.value })}>
              <option value="">Não informado</option>
              <option value="simples">Simples Nacional (usa CSOSN)</option>
              <option value="normal">Regime normal: Lucro Presumido ou Real (usa CST)</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="stockmode">Pedidos e estoque</label>
            <select className="select" id="stockmode" disabled={!canEdit} value={f.orderStockMode} onChange={(e) => setF({ ...f, orderStockMode: e.target.value })}>
              <option value="">Automático (agora: {q.data?.orderStockModeEffective === 'reserve' ? 'reservar até o meu sistema atualizar' : q.data?.orderStockModeEffective === 'off' ? 'não mexer' : 'a Berytus baixa'})</option>
              <option value="deduct">A Berytus baixa o estoque a cada venda (não tenho ERP integrado)</option>
              <option value="reserve">Reservar até o meu sistema mandar o estoque atualizado (tenho ERP ou integração)</option>
              <option value="off">Pedidos não mexem no estoque</option>
            </select>
            <span className="small muted">Vale para pedidos novos. Pedidos do histórico e do Full nunca mexem no estoque da loja.</span>
          </div>
          <div className="field">
            <label htmlFor="xmlurl">Link do XML da nota no seu ERP</label>
            <input className="input" id="xmlurl" disabled={!canEdit} value={f.invoiceXmlUrl} maxLength={500}
              placeholder="http://seu-erp:porta/api/.../GetXMLOrder?invoiceKey={chave}" onChange={(e) => setF({ ...f, invoiceXmlUrl: e.target.value })} />
            <span className="small muted">
              Com o link, basta informar a <b>chave da nota</b> (pela API ou no pedido) e a Berytus baixa o XML daqui e envia ao canal.
              Use <code>{'{chave}'}</code> onde vai a chave de acesso.
            </span>
          </div>
          <h2 style={{ margin: '8px 0 0' }}>Endereço</h2>
          <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            {ADDR.map((a) => (
              <div className="field" key={a.k}>
                <label htmlFor={`a-${a.k}`}>{a.label}</label>
                <input className="input" id={`a-${a.k}`} maxLength={a.max} disabled={!canEdit} value={f.address[a.k] ?? ''}
                  onChange={(e) => setF({ ...f, address: { ...f.address, [a.k]: a.k === 'state' ? e.target.value.toUpperCase() : e.target.value } })} />
              </div>
            ))}
          </div>
          {save.error && <ErrorBox error={save.error} />}
          {canEdit && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className="btn primary" type="submit" disabled={save.isPending}>Salvar</button>
              {save.isSuccess && <span className="small muted">Salvo.</span>}
            </div>
          )}
        </form>

        <div className="panel" style={{ display: 'grid', gap: 10 }}>
          <h2 style={{ margin: 0 }}>Plano</h2>
          <div><Tag tone={c.plan === 'trial' ? (t?.expired ? 'off' : 'warn') : 'ok'}>{PLAN[c.plan]}</Tag></div>
          {t ? (
            <>
              <p style={{ margin: 0 }}>
                {t.expired ? <>O teste terminou em <b>{dateTime(t.endsAt)}</b>.</> : <>Termina em <b>{dateTime(t.endsAt)}</b> ({t.daysLeft === 1 ? 'último dia' : `${int(t.daysLeft)} dias`}).</>}
              </p>
              <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                <li>Produtos: {int(t.usage.products)} de {int(t.limits.products ?? 0)} (kits não contam)</li>
                <li>Até {int(t.limits.listingsPerChannel ?? 0)} anúncios criados pela Berytus em cada marketplace</li>
                <li>Todos os marketplaces liberados para conectar</li>
              </ul>
              <p className="small muted" style={{ margin: 0 }}>Para contratar, fale com <a href="mailto:contato@berytus.com.br">contato@berytus.com.br</a>.</p>
            </>
          ) : <p className="small muted" style={{ margin: 0 }}>Sem limites de produtos ou anúncios.</p>}
          <p className="small muted" style={{ margin: 0 }}>Conta criada em {dateTime(c.createdAt)}.</p>
        </div>
      </div>
    </>
  );
}
